/* [PATCH pregled-poslovanja-fe v1] Aplikacija Pregled poslovanja — privzeta plošča (F1).
   Načrt: claude/NACRT_pregled_poslovanja_v2_20260927.md. Vse številke iz GET /pregled-poslovanja/{cid}/* (samo branje).
   Drill-down (P1): seznam računov v Pregledu; »Odpri v Nabavi/Prodaji« samo, če ima uporabnik tisto aplikacijo. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, ComposedChart,
} from 'recharts';
import { X, Loader, Info, TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { fmt } from '../lib/izdajaZneski';
import { useMojeAplikacije } from '../hooks/useAplikacija';
import { shraniAplikacijo } from '../aplikacije';

/* [PATCH prodaja-pregled-fe v1] skupni gradniki izvozeni za ProdajaPregledPage (vedenje nespremenjeno) */
export type Preset = 'mesec' | 'prejsnji_mesec' | 'cetrtletje' | 'letos' | 'lani' | '12m' | 'po_meri';
export type Primerjava = 'prejsnje' | 'lani' | 'brez';
type Razcl = 'razred' | 'konto' | 'sm' | 'projekt';

export const BARVA = { prihodki: '#16a34a', nabava: '#ea580c', rezultat: '#059669', obr: '#7c3aed', odb: '#0ea5e9', rok: '#e11d48' }; /* [PATCH pregled-barve v1] prihodki zelena, nabava oranzna, DDV obracunani vijolicna */
const MESECI = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'];
export const PRESETI: { id: Preset; naziv: string }[] = [
  { id: 'mesec', naziv: 'Ta mesec' }, { id: 'prejsnji_mesec', naziv: 'Prejšnji mesec' },
  { id: 'cetrtletje', naziv: 'To četrtletje' }, { id: 'letos', naziv: 'Letos' }, { id: 'lani', naziv: 'Lani' },
  { id: '12m', naziv: 'Zadnjih 12 mesecev' }, { id: 'po_meri', naziv: 'Po meri' },
];
const RAZCL: { id: Razcl; naziv: string }[] = [
  { id: 'razred', naziv: 'Vrsta' }, { id: 'konto', naziv: 'Konto' }, { id: 'sm', naziv: 'Stroškovno mesto' }, { id: 'projekt', naziv: 'Projekt' },
];

export const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const fmtDatum = (s?: string | null) => {
  if (!s) return '—';
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(s);
};
export const mesecOznaka = (k: string) => { const [y, m] = k.split('-'); return MESECI[Number(m) - 1] + ' ' + y.slice(2); };
export const eur = (v: any) => fmt(Number(v || 0)) + ' €';
export const kratko = (v: any) => {
  const n = Number(v || 0); const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' mio';
  if (a >= 1e3) return (n / 1e3).toLocaleString('de-DE', { maximumFractionDigits: 0 }) + ' k';
  return String(Math.round(n));
};
export const plusDni = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export function obdobjeZa(p: Preset, danes: Date): [string, string] {
  const y = danes.getFullYear(); const m = danes.getMonth();
  switch (p) {
    case 'mesec': return [iso(new Date(y, m, 1)), iso(danes)];
    case 'prejsnji_mesec': return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
    case 'cetrtletje': return [iso(new Date(y, Math.floor(m / 3) * 3, 1)), iso(danes)];
    case 'lani': return [iso(new Date(y - 1, 0, 1)), iso(new Date(y - 1, 11, 31))];
    case '12m': return [iso(new Date(y, m - 11, 1)), iso(danes)];
    default: return [iso(new Date(y, 0, 1)), iso(danes)];
  }
}
export function minusLeto(s: string) {
  const [y, m, d] = s.split('-').map(Number);
  const dd = m === 2 && d === 29 ? 28 : d;
  return `${y - 1}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

export function Delta({ zdaj, prej, st, obrnjeno }: { zdaj: number; prej: number; st: number; obrnjeno?: boolean }) {
  if (!st) return <span className="text-xs text-gray-400">Za primerjavo ni podatkov</span>;
  if (Math.abs(prej) < 0.005) return <span className="text-xs text-gray-400">primerjava: 0,00 €</span>;
  const p = ((zdaj - prej) / Math.abs(prej)) * 100;
  const gor = p >= 0;
  const dobro = obrnjeno ? !gor : gor;
  return (
    <span className={'text-xs inline-flex items-center gap-1 ' + (dobro ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
      {gor ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {(gor ? '+' : '') + p.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %
      <span className="text-gray-400">(primerjava {eur(prej)})</span>
    </span>
  );
}

export function Kartica({ naslov, vrednost, opomba, children, onClick }: any) {
  return (
    <div onClick={onClick}
      className={'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 ' + (onClick ? 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500' : '')}>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{naslov}</div>
      <div className="text-xl font-semibold mt-1 text-gray-900 dark:text-white">{vrednost}</div>
      {opomba && <div className="text-[11px] text-gray-400 mt-0.5">{opomba}</div>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function Okvir({ naslov, opomba, desno, children, className }: any) {
  return (
    <div className={'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 ' + (className || '')}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">{naslov}</div>
          {opomba && <div className="text-[11px] text-gray-400 mt-0.5">{opomba}</div>}
        </div>
        {desno}
      </div>
      {children}
    </div>
  );
}

export const Prazno = ({ besedilo }: { besedilo?: string }) => (
  <div className="text-xs text-gray-400 py-6 text-center">{besedilo || 'Za izbrano obdobje ni podatkov.'}</div>
);

export function Lestvica({ tocke, ostalo, barva, onKlik, oznaka }: any) {
  const vse: any[] = [...(tocke || []), ...(ostalo ? [ostalo] : [])];
  if (!vse.length) return <Prazno />;
  const max = Math.max(0, ...vse.map((t: any) => Math.abs(t.vrednost))); /* [PATCH pregled-lestvica v1] merilo = največja dejanska vrednost */
  return (
    <div className="space-y-2">
      {vse.map((t: any, i: number) => (
        <button key={i} type="button" disabled={t.kljuc == null} onClick={() => t.kljuc != null && onKlik && onKlik(t)}
          className="w-full text-left group disabled:cursor-default">
          <div className="flex justify-between text-xs gap-2">
            <span className="truncate text-gray-700 dark:text-gray-300 group-enabled:group-hover:text-blue-600">{oznaka ? oznaka(t) : (t.naziv || t.kljuc)}</span>
            <span className="font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{eur(t.vrednost)}</span>
          </div>
          <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-800 mt-1">
            <div className="h-1.5 rounded" style={{ width: `${(!max || !t.vrednost) ? 0 : Math.max(2, (Math.abs(t.vrednost) / max) * 100)}%`, background: barva }} />
          </div>
        </button>
      ))}
    </div>
  );
}

export function koncentracija(s: any) {
  if (!s || !s.tocke || !s.tocke.length) return null;
  const skupaj = s.tocke.reduce((a: number, t: any) => a + t.vrednost, 0) + (s.ostalo ? s.ostalo.vrednost : 0);
  if (Math.abs(skupaj) < 0.005) return null;
  const n = Math.min(3, s.tocke.length);
  const top = s.tocke.slice(0, n).reduce((a: number, t: any) => a + t.vrednost, 0);
  return `Prvih ${n} = ${Math.round((top / skupaj) * 100)} % zneska`;
}

/* [PATCH pregled-ddv-racuni-fe v1] tabela za drill-down stolpca grafa DDV */
function DdvTabela({ racuni }: { racuni: any[] }) {
  if (!racuni.length) return <Prazno besedilo="Ni računov za ta izbor." />;
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-gray-500">
        <tr>
          <th className="text-left px-3 py-2">Datum DDV</th>
          <th className="text-left px-2 py-2">Številka</th>
          <th className="text-left px-2 py-2">Interna</th>
          <th className="text-left px-2 py-2">Smer</th>
          <th className="text-left px-2 py-2">Partner</th>
          <th className="text-right px-2 py-2">Osnova</th>
          <th className="text-right px-2 py-2">DDV</th>
          <th className="text-left px-2 py-2">Vrste DDV</th>
          <th className="text-left px-3 py-2">Stanje</th>
        </tr>
      </thead>
      <tbody>
        {racuni.map((r: any) => (
          <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
            <td className="px-3 py-1.5 whitespace-nowrap">{fmtDatum(r.datum)}</td>
            <td className="px-2 py-1.5">{r.stevilka || '—'}{(r.doc_type === 'izdani_dobropis' || r.doc_type === 'credit_note') && <span className="ml-1 text-[10px] text-rose-600">dobropis</span>}</td>
            <td className="px-2 py-1.5 text-gray-500">{r.interna || ''}</td>
            <td className="px-2 py-1.5 text-gray-500">{String(r.doc_type || '').startsWith('izdani') ? 'izdani' : 'prejeti'}</td>
            <td className="px-2 py-1.5 truncate max-w-[12rem]">{r.partner || '—'}</td>
            <td className="px-2 py-1.5 text-right font-mono">{fmt(r.osnova)}</td>
            <td className="px-2 py-1.5 text-right font-mono">{fmt(r.ddv)}</td>
            <td className="px-2 py-1.5 text-gray-500">{r.vrste}</td>
            <td className="px-3 py-1.5 text-gray-500">{r.status === 'approved' ? 'potrjen' : 'nepotrjen'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const tooltipStil = { fontSize: 12, borderRadius: 8 };

export function PregledPage() {
  const { selectedCompany } = useAuthStore();
  const cid = selectedCompany?.id;
  const navigate = useNavigate();
  const { dovoljene } = useMojeAplikacije();

  const [preset, setPreset] = useState<Preset>('letos');
  const [odMeri, setOdMeri] = useState('');
  const [doMeri, setDoMeri] = useState('');
  const [primerjava, setPrimerjava] = useState<Primerjava>('lani');
  const [bruto, setBruto] = useState(false);
  const [samoPotrjeni, setSamoPotrjeni] = useState(false);
  const [razcl, setRazcl] = useState<Razcl>('razred');
  const [podatki, setPodatki] = useState<any>(null);
  const [nalaganje, setNalaganje] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [drill, setDrill] = useState<any>(null);

  const [od, doDat] = useMemo<[string, string]>(
    () => (preset === 'po_meri' && odMeri && doMeri ? [odMeri, doMeri] : obdobjeZa(preset, new Date())),
    [preset, odMeri, doMeri]);

  const nalozi = useCallback(async () => {
    if (!cid) return;
    setNalaganje(true); setNapaka(null);
    const pot = `/pregled-poslovanja/${cid}`;
    const osn: any = { od, 'do': doDat, samo_potrjeni: samoPotrjeni };
    const b: any = { ...osn, bruto };
    const lani: any = { ...b, od: minusLeto(od), 'do': minusLeto(doDat) };
    try {
      const r = await Promise.all([
        api.get(`${pot}/stanje`),
        api.get(`${pot}/povzetek`, { params: b }),
        api.get(`${pot}/ddv`, { params: osn }),
        api.get(`${pot}/obveznosti`, { params: { samo_potrjeni: samoPotrjeni } }),
        api.get(`${pot}/serija`, { params: { ...b, mera: 'prihodki', razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...b, mera: 'nabava', razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...b, mera: 'prihodki', razclenitev: 'partner', limit: 10 } }),
        api.get(`${pot}/serija`, { params: { ...b, mera: 'nabava', razclenitev: 'partner', limit: 10 } }),
        api.get(`${pot}/serija`, { params: { ...osn, mera: 'nabava', razclenitev: razcl, limit: 12 } }),
        api.get(`${pot}/serija`, { params: { ...lani, mera: 'prihodki', razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...lani, mera: 'nabava', razclenitev: 'mesec' } }),
      ]);
      setPodatki({
        stanje: r[0].data, povzetek: r[1].data, ddv: r[2].data, obveznosti: r[3].data,
        prihMes: r[4].data, nabMes: r[5].data, kupci: r[6].data, dobavitelji: r[7].data, stroski: r[8].data,
        prihMesL: r[9].data, nabMesL: r[10].data,
      });
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Podatkov ni bilo mogoče naložiti.');
    } finally {
      setNalaganje(false);
    }
  }, [cid, od, doDat, bruto, samoPotrjeni, razcl]);

  useEffect(() => { nalozi(); }, [nalozi]);

  // drill-down: naloži seznam, ko se odpre
  useEffect(() => {
    if (!drill || !drill.nalaganje || !cid) return;
    (drill.params?.ddv /* [PATCH pregled-ddv-racuni-fe v1] */
      ? api.get(`/pregled-poslovanja/${cid}/ddv-racuni`, { params: { vrsta: drill.params.ddv, mesec: drill.params.mesec, samo_potrjeni: samoPotrjeni } })
      : api.get(`/pregled-poslovanja/${cid}/racuni`, { params: { od, 'do': doDat, samo_potrjeni: samoPotrjeni, ...drill.params } }))
      .then((r: any) => setDrill((d: any) => d && { ...d, podatki: r.data, nalaganje: false }))
      .catch((e: any) => setDrill((d: any) => d && { ...d, napaka: e?.response?.data?.detail || 'Seznama ni bilo mogoče naložiti.', nalaganje: false }));
  }, [drill, cid, od, doDat, samoPotrjeni]);
  const odpri = (naslov: string, params: any) => setDrill({ naslov, params, podatki: null, nalaganje: true });

  const mesecni = useMemo(() => {
    if (!podatki) return [];
    const p = podatki.prihMes.tocke || []; const n = podatki.nabMes.tocke || [];
    const pl = podatki.prihMesL?.tocke || []; const nl = podatki.nabMesL?.tocke || [];
    let kp = 0; let kn = 0; let kpl = 0; let knl = 0;
    return p.map((t: any, i: number) => {
      const nv = n[i]?.vrednost || 0; const plv = pl[i]?.vrednost || 0; const nlv = nl[i]?.vrednost || 0;
      kp += t.vrednost; kn += nv; kpl += plv; knl += nlv;
      return { kljuc: t.kljuc, oznaka: mesecOznaka(t.kljuc), prihodki: t.vrednost, nabava: nv, prihodkiL: plv, nabavaL: nlv,
               kumP: kp, kumN: kn, kumPL: kpl, kumNL: knl };
    });
  }, [podatki]);

  if (!cid) {
    return <div className="p-6 text-sm text-gray-500">Najprej izberite podjetje v zgornjem izbirniku.</div>;
  }

  const pv = podatki?.povzetek;
  const ref = pv ? (primerjava === 'lani' ? pv.lani : primerjava === 'prejsnje' ? pv.prejsnje : null) : null;
  const imaLani = !!pv && (pv.lani.st_izdanih + pv.lani.st_prejetih) > 0;
  const pokaziLani = primerjava === 'lani' && imaLani;
  const st = podatki?.stanje;
  const ddv = podatki?.ddv;
  const obv = podatki?.obveznosti;
  const danes = new Date();

  const kosiObv: any[] = obv && obv.na_voljo ? [
    { kljuc: 'ni_zapadlo', naziv: 'Še ni zapadlo', params: { rok_od: iso(danes) } },
    { kljuc: 'd1_30', naziv: 'Zapadlo 1–30 dni', params: { rok_od: iso(plusDni(danes, -30)), rok_do: iso(plusDni(danes, -1)) } },
    { kljuc: 'd31_60', naziv: 'Zapadlo 31–60 dni', params: { rok_od: iso(plusDni(danes, -60)), rok_do: iso(plusDni(danes, -31)) } },
    { kljuc: 'd61_90', naziv: 'Zapadlo 61–90 dni', params: { rok_od: iso(plusDni(danes, -90)), rok_do: iso(plusDni(danes, -61)) } },
    { kljuc: 'nad_90', naziv: 'Zapadlo več kot 90 dni', params: { rok_do: iso(plusDni(danes, -91)) } },
  ].map((k) => ({ ...k, vrednost: obv.kosi[k.kljuc].znesek, st: obv.kosi[k.kljuc].st })) : [];

  const drillCilj = (drill?.params?.smer === 'izdani' || drill?.params?.ddv === 'obracunani') ? 'prodaja' : 'nabava'; /* [PATCH pregled-ddv-racuni-fe v1] */
  const smeVCilj = (dovoljene as string[]).includes(drillCilj);

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* glava + filtri */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Pregled poslovanja</h1>
          <div className="text-xs text-gray-500">{(selectedCompany as any)?.name} · {fmtDatum(od)} – {fmtDatum(doDat)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="flex flex-wrap rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {PRESETI.map((p) => (
              <button key={p.id} type="button" onClick={() => setPreset(p.id)}
                className={'px-2.5 py-1.5 ' + (preset === p.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}>
                {p.naziv}
              </button>
            ))}
          </div>
          {preset === 'po_meri' && (
            <span className="flex items-center gap-1">
              <input type="date" value={odMeri} onChange={(e) => setOdMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
              –
              <input type="date" value={doMeri} onChange={(e) => setDoMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
            </span>
          )}
          <select value={primerjava} onChange={(e) => setPrimerjava(e.target.value as Primerjava)}
            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <option value="lani">Primerjava: isto obdobje lani</option>
            <option value="prejsnje">Primerjava: prejšnje obdobje</option>
            <option value="brez">Brez primerjave</option>
          </select>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={bruto} onChange={(e) => setBruto(e.target.checked)} /> z DDV</label>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={samoPotrjeni} onChange={(e) => setSamoPotrjeni(e.target.checked)} /> samo potrjeni</label>
          {nalaganje && <Loader className="w-4 h-4 animate-spin text-blue-500" />}
        </div>
      </div>

      {napaka && <div className="rounded-lg bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300 text-sm p-3">{napaka}</div>}

      {/* vrstica popolnosti */}
      {st && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 bg-gray-100 dark:bg-gray-800/60 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5" />
          <span>Zadnji vnos: prejeti {fmtDatum(st.zadnji_vnos_prejeti)}, izdani {fmtDatum(st.zadnji_vnos_izdani)}</span>
          <span>Nepotrjenih: {st.nepotrjenih}{samoPotrjeni ? ' (izključeni)' : ' (vključeni)'}</span>
          {st.izven_razpona > 0 && <span className="text-amber-600">Z datumom izven razpona: {st.izven_razpona}</span>}
          <span>Rezultat po računih ni poslovni izid (plače, amortizacija in stroški brez računa niso zajeti).</span>
        </div>
      )}

      {pv && (
        <>
          {/* kartice */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kartica naslov={'Prihodki' + (bruto ? ' (z DDV)' : '')} vrednost={eur(pv.obdobje.prihodki)} opomba={`${pv.obdobje.st_izdanih} izdanih`}
              onClick={() => odpri('Izdani računi', { smer: 'izdani' })}>
              {ref && <Delta zdaj={pv.obdobje.prihodki} prej={ref.prihodki} st={ref.st_izdanih} />}
            </Kartica>
            <Kartica naslov={'Nabava' + (bruto ? ' (z DDV)' : '')} vrednost={eur(pv.obdobje.nabava)} opomba={`${pv.obdobje.st_prejetih} prejetih` + (bruto ? '' : ` · vključno z ${pv.obdobje.nabava_vkljucuje || 'neodbitnim DDV'}`) /* [PATCH pregled-strosek-fe v1] */}
              onClick={() => odpri('Prejeti računi', { smer: 'prejeti' })}>
              {ref && <Delta zdaj={pv.obdobje.nabava} prej={ref.nabava} st={ref.st_prejetih} obrnjeno />}
            </Kartica>
            <Kartica naslov="Rezultat po računih" vrednost={eur(pv.obdobje.rezultat)} opomba="brez DDV, brez investicij">
              {ref && <Delta zdaj={pv.obdobje.rezultat} prej={ref.rezultat} st={ref.st_izdanih + ref.st_prejetih} />}
            </Kartica>
            <Kartica naslov="Investicije" vrednost={eur(pv.obdobje.investicije)} opomba="knjižbe na kontih razreda 0, brez DDV"
              onClick={() => odpri('Investicije (razred 0)', { smer: 'prejeti', razred: '0' })} />
            <Kartica naslov="Ocena DDV za obdobje"
              vrednost={ddv && ddv.na_voljo ? eur(ddv.skupaj.za_placilo) : '—'}
              opomba={ddv && ddv.na_voljo ? 'obračunani − odbitni' + (ddv.skupaj.oss ? ` · OSS ločeno ${eur(ddv.skupaj.oss)}` : '') : (ddv?.razlog || '')} />
            <Kartica naslov="Obveznosti v 7 dneh"
              vrednost={obv && obv.na_voljo ? eur(obv.zapade_7.znesek) : '—'}
              opomba={obv && obv.na_voljo ? `zapadlo neplačano: ${eur(obv.zapadlo.znesek)}` : 'ni na voljo'}
              onClick={obv && obv.na_voljo ? () => odpri('Obveznosti — zapade v 7 dneh', { smer: 'prejeti', odprti: true, rok_od: iso(danes), rok_do: iso(plusDni(danes, 7)) }) : undefined} />
          </div>

          {/* prihodki vs nabava */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Prihodki in nabava po mesecih" opomba={(bruto ? 'z DDV' : 'brez DDV (nabava z neodbitnim DDV)') /* [PATCH pregled-strosek-fe v1] */ + ' · os: datum storitve · klik na stolpec odpre račune' + (pokaziLani ? ' · črtkano: lani' : '')}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="prihodki" name="Prihodki" fill={BARVA.prihodki} cursor="pointer"
                      onClick={(d: any) => { const e = d?.payload || d; odpri('Izdani računi — ' + e.oznaka, { smer: 'izdani', mesec: e.kljuc }); }} />
                    <Bar dataKey="nabava" name="Nabava" fill={BARVA.nabava} cursor="pointer"
                      onClick={(d: any) => { const e = d?.payload || d; odpri('Prejeti računi — ' + e.oznaka, { smer: 'prejeti', mesec: e.kljuc }); }} />
                    {pokaziLani && <Line dataKey="prihodkiL" name="Prihodki lani" stroke={BARVA.prihodki} strokeDasharray="5 4" dot={false} />}
                    {pokaziLani && <Line dataKey="nabavaL" name="Nabava lani" stroke={BARVA.nabava} strokeDasharray="5 4" dot={false} />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Okvir>

            <Okvir naslov="Kumulativa v obdobju" opomba={pokaziLani ? 'polno: letos · črtkano: lani' : 'seštevek po mesecih'}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="kumP" name="Prihodki" stroke={BARVA.prihodki} strokeWidth={2} dot={false} />
                    <Line dataKey="kumN" name="Nabava" stroke={BARVA.nabava} strokeWidth={2} dot={false} />
                    {pokaziLani && <Line dataKey="kumPL" name="Prihodki lani" stroke={BARVA.prihodki} strokeDasharray="5 4" dot={false} />}
                    {pokaziLani && <Line dataKey="kumNL" name="Nabava lani" stroke={BARVA.nabava} strokeDasharray="5 4" dot={false} />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Okvir>
          </div>

          {/* DDV + stroški */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="DDV po mesecih" opomba="os: datum DDV · obračunani (izdani + samoobdavčitev) − odbitni (prejeti) · OSS ni vključen · klik na stolpec odpre račune">
              {ddv && ddv.na_voljo ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={(ddv.meseci || []).map((m: any) => ({ ...m, oznaka: mesecOznaka(m.mesec) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                      <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="obracunani" name="Obračunani" fill={BARVA.obr} cursor="pointer" /* [PATCH pregled-ddv-racuni-fe v1] */
                        onClick={(d: any) => { const e = d?.payload || d; odpri('DDV obračunani — ' + e.oznaka, { ddv: 'obracunani', mesec: e.mesec }); }} />
                      <Bar dataKey="odbitni" name="Odbitni" fill={BARVA.odb} cursor="pointer"
                        onClick={(d: any) => { const e = d?.payload || d; odpri('DDV odbitni — ' + e.oznaka, { ddv: 'odbitni', mesec: e.mesec }); }} />
                      <Line dataKey="za_placilo" name="Za plačilo" stroke={BARVA.rok} strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : <Prazno besedilo={ddv?.razlog || 'DDV ni na voljo.'} />}
            </Okvir>

            <Okvir naslov="Kam gre denar" opomba="nabava po knjižbah, brez DDV · klik odpre račune"
              desno={
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
                  {RAZCL.map((r) => (
                    <button key={r.id} type="button" onClick={() => setRazcl(r.id)}
                      className={'px-2 py-1 ' + (razcl === r.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300')}>
                      {r.naziv}
                    </button>
                  ))}
                </div>
              }>
              <Lestvica tocke={podatki.stroski.tocke} ostalo={podatki.stroski.ostalo} barva={BARVA.nabava}
                oznaka={(t: any) => (razcl === 'konto' ? `${t.kljuc} ${t.naziv || ''}` : (t.naziv || t.kljuc))}
                onKlik={(t: any) => odpri('Nabava — ' + (razcl === 'konto' ? t.kljuc : t.naziv), { smer: 'prejeti', [razcl]: t.kljuc })} />
            </Okvir>
          </div>

          {/* partnerji */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Največji kupci" opomba={koncentracija(podatki.kupci) || 'prihodki po kupcu'}>
              <Lestvica tocke={podatki.kupci.tocke} ostalo={podatki.kupci.ostalo} barva={BARVA.prihodki}
                onKlik={(t: any) => odpri('Izdani računi — ' + t.naziv, { smer: 'izdani', partner: t.kljuc })} />
            </Okvir>
            <Okvir naslov="Največji dobavitelji" opomba={koncentracija(podatki.dobavitelji) || 'nabava po dobavitelju'}>
              <Lestvica tocke={podatki.dobavitelji.tocke} ostalo={podatki.dobavitelji.ostalo} barva={BARVA.nabava}
                onKlik={(t: any) => odpri('Prejeti računi — ' + t.naziv, { smer: 'prejeti', partner: t.kljuc })} />
            </Okvir>
          </div>

          {/* obveznosti */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Starost neplačanih obveznosti" opomba="prejeti računi, upošteva plačila, označena v aplikaciji">
              {obv && obv.na_voljo
                ? <Lestvica tocke={kosiObv} barva={BARVA.rok}
                    oznaka={(t: any) => `${t.naziv} (${t.st})`}
                    onKlik={(t: any) => odpri('Obveznosti — ' + t.naziv, { smer: 'prejeti', odprti: true, ...t.params })} />
                : <Prazno besedilo={(obv?.razlog || 'Ni na voljo.') + (obv ? ` Označenih plačil med zapadlimi v zadnjih 90 dneh: ${Math.round((obv.delez_placanih_90 || 0) * 100)} % (prag ${Math.round((obv.prag || 0.5) * 100)} %).` : '')} />}
            </Okvir>
            <Okvir naslov="Koledar plačil — naslednjih 30 dni" opomba="neplačani prejeti po datumu zapadlosti · klik odpre račune">
              {obv && obv.na_voljo ? (
                (obv.koledar || []).length ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(obv.koledar || []).map((k: any) => ({ ...k, oznaka: fmtDatum(k.datum).slice(0, 6) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                        <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                        <Bar dataKey="znesek" name="Za plačilo" fill={BARVA.rok} cursor="pointer"
                          onClick={(d: any) => { const e = d?.payload || d; odpri('Obveznosti — rok ' + fmtDatum(e.datum), { smer: 'prejeti', odprti: true, rok_od: e.datum, rok_do: e.datum }); }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <Prazno besedilo="V naslednjih 30 dneh ni zapadlih neplačanih obveznosti." />
              ) : <Prazno besedilo="Ni na voljo (glej levo)." />}
            </Okvir>
          </div>
        </>
      )}

      {/* drill-down panel */}
      {drill && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDrill(null)}>
          <div className="h-full w-full max-w-3xl bg-white dark:bg-gray-900 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{drill.naslov}</div>
                <div className="text-[11px] text-gray-400">{drill.params?.ddv ? `obdobje DDV ${drill.params.mesec} (po datumu DDV)` /* [PATCH pregled-ddv-racuni-fe v1] */ : drill.params?.odprti ? 'odprte postavke po zapadlosti' : `${fmtDatum(od)} – ${fmtDatum(doDat)}`}</div>
              </div>
              <div className="flex items-center gap-2">
                {smeVCilj && (
                  <button type="button" onClick={() => {
                    shraniAplikacijo(drillCilj as any);
                    navigate(drillCilj === 'prodaja' ? '/dokumenti?tip=izdani_racun' : '/dokumenti?tip=prejeti_racun');
                  }} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <ExternalLink className="w-3.5 h-3.5" /> Odpri v {drillCilj === 'prodaja' ? 'Prodaji' : 'Nabavi'}
                  </button>
                )}
                <button type="button" onClick={() => setDrill(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {drill.nalaganje && <div className="p-6 flex justify-center"><Loader className="w-6 h-6 animate-spin text-blue-500" /></div>}
              {drill.napaka && <div className="p-4 text-sm text-rose-600">{drill.napaka}</div>}
              {drill.podatki && drill.params?.ddv && <DdvTabela racuni={drill.podatki.racuni} />}{/* [PATCH pregled-ddv-racuni-fe v1] */}
              {drill.podatki && !drill.params?.ddv && (
                drill.podatki.racuni.length ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">{drill.params?.odprti ? 'Rok' : 'Datum'}</th>
                        <th className="text-left px-2 py-2">Številka</th>
                        <th className="text-left px-2 py-2">Interna</th>
                        <th className="text-left px-2 py-2">Partner</th>
                        <th className="text-right px-2 py-2">Osnova</th>
                        <th className="text-right px-2 py-2">Skupaj</th>
                        {drill.podatki.smer === 'prejeti' && <th className="text-right px-2 py-2">Odprto</th>}
                        <th className="text-left px-3 py-2">Stanje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drill.podatki.racuni.map((r: any) => (
                        <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDatum(drill.params?.odprti ? r.rok : r.datum)}</td>
                          <td className="px-2 py-1.5">{r.stevilka || '—'}{(r.doc_type === 'izdani_dobropis' || r.doc_type === 'credit_note') && <span className="ml-1 text-[10px] text-rose-600">dobropis</span>}</td>
                          <td className="px-2 py-1.5 text-gray-500">{r.interna || ''}</td>
                          <td className="px-2 py-1.5 truncate max-w-[14rem]">{r.partner || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.osnova)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.skupaj)}</td>
                          {drill.podatki.smer === 'prejeti' && <td className="px-2 py-1.5 text-right font-mono">{fmt(r.odprto)}</td>}
                          <td className="px-3 py-1.5 text-gray-500">{r.status === 'approved' ? 'potrjen' : 'nepotrjen'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Prazno besedilo="Ni računov za ta izbor." />
              )}
            </div>
            {drill.podatki && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-4">
                <span>Računov: {drill.podatki.st}{drill.podatki.omejeno ? ` (prikazanih ${drill.podatki.racuni.length})` : ''}</span>
                <span>Osnova: {eur(drill.podatki.osnova)}</span>
                {drill.podatki.ddv != null ? <span>DDV: {eur(drill.podatki.ddv)}</span> : <span>Skupaj: {eur(drill.podatki.skupaj)}</span>}{/* [PATCH pregled-ddv-racuni-fe v1] */}
                {drill.podatki.strosek != null && <span>Strošek (z neodbitnim DDV): {eur(drill.podatki.strosek)}</span>}{/* [PATCH pregled-strosek-fe v1] */}
                {drill.podatki.odprto != null && <span>Odprto: {eur(drill.podatki.odprto)}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
